import { VisuallyHidden } from '@chakra-ui/react';
import {
  IPriceResSchema,
  IQuoteItemRespSchema,
  PriceModelEnum,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import { formatNumber } from '@monetize/utils/core';
import { BiSolidChevronsDown } from 'react-icons/bi';
import { OFFERINGS } from '../../../constants';
import {
  MBox,
  MCheckbox,
  MCustomIconButton,
  MFlex,
  MHStack,
  MText,
  MTooltip,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { MPercent } from '../../Monetize/MPercent';

interface SortedProductsWithPricesRowProps {
  id: string;
  prices: IPriceResSchema[];
  isPoTOfferingType: boolean;
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  currency: string;
  handleCheckboxChange: (id: string, priceId: string, checked: boolean) => void;
  isChecked: (id: string, priceId: string) => boolean;
  onSelectFromTier: (tierIndex: number) => void;
  hasMultipleTiers: boolean;
  disabled?: boolean;
}

export const SortedProductsWithPricesRow = ({
  id,
  prices,
  isPoTOfferingType,
  productsObj,
  currency,
  handleCheckboxChange,
  isChecked,
  onSelectFromTier,
  hasMultipleTiers,
  disabled,
}: SortedProductsWithPricesRowProps) => {
  return prices.map(
    (
      { from, to, priceModel, amount, product, id: priceId },
      productIndex: number,
    ) => {
      const formattedFrom = formatNumber(from, {
        maximumFractionDigits: 0,
      });

      const formattedTo =
        formatNumber(to, {
          maximumFractionDigits: 0,
        }) || '∞';

      const isLastRow = prices.length - 1 === productIndex;

      return (
        <MHStack
          key={`${productIndex}-range-${id}`}
          align="flex-start"
          gap={4}
          mb={!isLastRow ? 2 : 0}
          role="group"
          className="group"
        >
          <MFlex alignItems="center">
            <MBox minW="7">
              {hasMultipleTiers && !isLastRow && (
                <MTooltip label="Select this and all below">
                  <MCustomIconButton
                    btnSize={5}
                    containerSize={5}
                    variant="icon"
                    mr="1"
                    icon={BiSolidChevronsDown}
                    onClick={() => onSelectFromTier(productIndex)}
                    aria-label="Select this and all below"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectFromTier(productIndex);
                      }
                    }}
                    _groupHover={{
                      display: 'block',
                    }}
                    display="none"
                    isDisabled={disabled}
                  >
                    <VisuallyHidden>Select this and all below</VisuallyHidden>
                  </MCustomIconButton>
                </MTooltip>
              )}
            </MBox>
            <MCheckbox
              py={2}
              isChecked={isChecked(id, priceId)}
              isDisabled={disabled}
              onChange={(e) => {
                handleCheckboxChange(id, priceId, e.target.checked);
              }}
            />
          </MFlex>
          <MFlex align="center" minW={4} minH={8}>
            <MText color="tPurple.base">{`${productIndex + 1}.`}</MText>
          </MFlex>
          <MFlex align="center" minH="8" minW="20">
            <MText truncate>{OFFERINGS.PRICE_MODEL_OPTIONS[priceModel]}</MText>
          </MFlex>
          <MFlex align="center" minH="8" minW="6.25rem">
            <MText>{formattedFrom}</MText>
          </MFlex>
          <MFlex align="center" minH="8" minW="6.25rem">
            <MText>{formattedTo}</MText>
          </MFlex>
          <MFlex align="center" minH="8" w="7.125rem">
            {isPoTOfferingType && <MPercent amount={amount} />}
            {!isPoTOfferingType && (
              <MCurrency
                amount={
                  priceModel === PriceModelEnum.CUSTOM
                    ? productsObj.byId[product.id]?.unitPrice
                    : amount
                }
                options={{
                  maximumFractionDigits: 'max',
                  currency,
                }}
              />
            )}
          </MFlex>
        </MHStack>
      );
    },
  );
};
